// [2026-09-02] ADDED — self-service API keys for the SIGNED-IN customer.
//
// Every call here is authenticated by the ACCOUNT SESSION (platform.*), not by an API key, so the
// server resolves the company from the login and the browser never names a client_id. That is what
// makes rotation safe: a leaked API key cannot rotate itself — only the signed-in owner can.
//
//   Create  -> POST /me/keys        (secret shown ONCE, never retrievable again)
//   Revoke  -> POST /me/keys/revoke   (the security action — logged with who, when and why)
//   Delete  -> POST /me/keys/delete   (removes the row; an ACTIVE key is revoked first, so the
//                                      revocation is logged before the record goes)
//   Rotate  -> create a new key, then revoke the old one. No window where nothing authenticates,
//              and credits are untouched because they belong to the company, not the key.
//
// RENAME is deliberately NOT here: the backend has no rename endpoint (a key's label is set at
// creation — app/modules/me/route.py). It is a small additive backend change; until it exists this
// screen does not pretend to offer it rather than showing a control that silently fails.
//
// [2026-09-04] CHANGED — restyled to the IncProctor Figma, and the create dialog can now be opened
// from the header via the `openCreateNonce` prop.
//
// NOT ONE LINE OF THE FIVE MUTATIONS CHANGED. create / revoke / remove / rotate and every
// confirmation string below are byte-for-byte what they were; only the chrome around them is new.
// That is deliberate — a visual revamp is exactly the wrong change to also quietly alter what a
// "Delete" button does to a live credential. The previous render block is kept at the bottom.

// [2026-09-06] ADDED — SDK keys and API keys, split into two tabs with a Create button each.
//
// A customer holds TWO credentials. They are byte-identical in format and differ only by the
// `service` field the API records: 'sdk' is used by the browser SDK on their exam page,
// 'proctoring' is used by their backend calling the proctoring API endpoint server-to-server.
// Before this the screen sent no service at all, so createKey defaulted to 'sdk' and there was no
// way to mint the second key at all.
//
// THE WIRE VALUE IS 'proctoring'; THE LABEL IS "API Keys". They differ deliberately — the backend
// name is fixed (app/core/apikeys.py:SERVICE_PROCTORING) and a customer thinks of this as the key
// for the API, not for "proctoring". Keep the two apart: `service` goes on the wire, `label` on
// the screen.
//
// BOTH CREATE BUTTONS ARE ALWAYS VISIBLE, not just the active tab's. Creating the key you are
// missing is the reason you came to this screen, and hiding it behind the tab you are NOT on is
// exactly the dead end that made the old screen unusable for API keys. The tabs filter the TABLE;
// they do not gate what you can create.

import { useEffect, useState } from 'react'
import { platform } from '../../api.js'
import { toast, useAsync } from '../../ui.jsx'
import { PfCard, PfChip, PfEmpty, PfModal, PfNotice, PfSkeleton } from '../../components/platform/ui.jsx'
// [2026-09-05] CHANGED — RotateIcon dropped here; the Rotate control is the supplied 48×48 PNG,
// which already CONTAINS its rounded-square border, so it is rendered as the whole button rather
// than as a glyph inside one (two borders would otherwise stack). RotateIcon is unchanged and
// still exported.
// OLD: CopyIcon, MinusCircleIcon, PlusIcon, RotateIcon, TrashIcon
import {
  CopyIcon, MinusCircleIcon, PlusIcon, TrashIcon,
} from '../../components/platform/icons.jsx'
import rotateKey from '../../assets/rotate-key.png'
// [2026-09-05] ADDED — replaces window.confirm(). See components/platform/confirm.jsx for why the
// browser dialog had to go: it cannot be styled, it blocks the event loop, and when a sandboxed
// frame suppresses it, it returns FALSE and the action silently never runs.
import { useConfirm } from '../../components/platform/confirm.jsx'
// [2026-09-25] ADDED — the same up/down sort arrow the credit ledger's "When" column uses.
import { PfSortHeader } from '../../components/platform/pagination.jsx'

// [2026-09-25] ADDED — order the keys by one of their dates, newest or oldest first.
//
// HONEST TO SORT HERE, IN THE BROWSER: GET /me/keys returns EVERY key in one response, with no
// paging (proctoring-sdk-backend app/modules/me/route.py:652-660), so this orders the whole list and
// not a visible slice. A key with no value for the date (an active key has no `revoked_at`) sorts
// LAST in both directions, so "NA" rows never sit above real dates. The dates are EPOCH SECONDS
// (app/core/apikeys.py writes time.time()), so they compare as numbers; text is the fallback only.
// Copied, not mutated — `keys` is derived from the fetched data.
function sortKeysBy(rows, sort) {
  if (!sort) return rows
  return [...rows].sort((a, b) => {
    const x = a?.[sort.key]
    const y = b?.[sort.key]
    if (x == null && y == null) return 0
    if (x == null) return 1
    if (y == null) return -1
    const cmp = typeof x === 'number' && typeof y === 'number'
      ? x - y
      : String(x).localeCompare(String(y), undefined, { numeric: true })
    return sort.dir === 'desc' ? -cmp : cmp
  })
}

const fmtDate = (epochSeconds) =>
  epochSeconds ? new Date(epochSeconds * 1000).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '—'

// [2026-09-04] CHANGED — the shadcn Badge variant map became a chip tone map.
// OLD: const STATUS_VARIANT = { active: 'healthy', revoked: 'idle' }
// The status is still rendered AS A WORD, never as a colour alone — see the badge rule in
// theme.css and the reason recorded beside it.
const STATUS_TONE = { active: 'live', revoked: 'idle' }

/** Copy to the clipboard, saying which value went. The catch matters: clipboard access is refused
 *  outright in some embedded browsers, and a Copy button that silently does nothing is how someone
 *  loses a secret that is shown exactly once. */
const copy = (text, what) => {
  navigator.clipboard?.writeText(text)
    .then(() => toast(`${what} copied`))
    .catch(() => toast('Copy failed — select and copy manually', true))
}

// The one-time reveal of a freshly minted key. Rendered from the create/rotate response and NEVER
// stored — closing it is the last time the secret is visible anywhere.
function SecretModal({ secret, onClose }) {
  return (
    <PfModal title="Your new API key" subtitle="Copy the secret now — it is shown only once."
             onClose={onClose}>
      <div className="pf-modal-warn">
        This secret is not stored and cannot be shown again. If you lose it, create another key.
      </div>

      <div className="pf-field-label">API key (X-API-Key)</div>
      <div className="pf-secret">
        <code>{secret.api_key}</code>
        <button className="pf-btn ghost" onClick={() => copy(secret.api_key, 'API key')}>
          <CopyIcon />Copy
        </button>
      </div>

      <div className="pf-field-label">Secret (X-API-Secret)</div>
      <div className="pf-secret">
        <code>{secret.secret}</code>
        <button className="pf-btn ghost" onClick={() => copy(secret.secret, 'Secret')}>
          <CopyIcon />Copy
        </button>
      </div>

      <div className="pf-modal-foot">
        <button className="pf-btn" onClick={onClose}>Done</button>
      </div>
    </PfModal>
  )
}

// The two key services, EXACTLY as app/core/apikeys.py:SERVICES spells them. Anything else is
// rejected server-side with `service must be one of ['sdk', 'proctoring']`.
const TABS = [
  { service: 'sdk', label: 'SDK Keys', create: 'Create SDK Key',
    blurb: 'Used by the browser SDK on your exam page to start and stream a proctored session.' },
  { service: 'proctoring', label: 'API Keys', create: 'Create API Key',
    blurb: 'Used by your backend to call the proctoring API endpoint server-to-server.' },
]

// A key stored before the service field existed has none. The API reads a missing value as 'sdk'
// and so does this, so keys created before the split stay on the tab they have always been on.
const serviceOf = (row) => (row.service || 'sdk')

// [2026-09-04] CHANGED — takes `openCreateNonce`. OLD signature: ({ onError }).
// Optional and defaults to 0, so a caller that does not pass it behaves exactly as before.
export default function PlatformKeys({ onError, openCreateNonce = 0 }) {
  const { data, error, loading, reload } = useAsync(() => platform.keys(), [])
  const [tab, setTab] = useState(TABS[0].service)
  // The service the OPEN create dialog will mint. Held separately from `tab` so that pressing
  // "Create API Key" while the SDK tab is showing mints an API key — the button says what it does.
  const [creatingService, setCreatingService] = useState(TABS[0].service)
  const [creating, setCreating] = useState(false)
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [secret, setSecret] = useState(null)   // the once-only reveal
  const [confirm, confirmDialog] = useConfirm()
  // [2026-09-25] ADDED — { key, dir } | null. null = the order the API returned. Above the early
  // returns below, like every other hook here.
  const [keySort, setKeySort] = useState(null)

  // [2026-09-04] ADDED — open the dialog when the header button is pressed.
  //
  // GUARDED ON `> 0` so the initial render does not pop a dialog at nobody: the nonce starts at 0
  // and this effect runs on mount. Every later value is a real press.
  useEffect(() => {
    // The app header's "Create API Key +" says API, so it mints an API key and shows that tab.
    if (openCreateNonce > 0) { setCreatingService('proctoring'); setTab('proctoring'); setCreating(true) }
  }, [openCreateNonce])

  // NOTE the early returns sit BELOW the hooks, not above them. Moving them up would make the
  // effect above conditional on the fetch having finished, and React would throw on the render
  // where `loading` flips.
  if (error) { onError?.(error); return <PfNotice kind="error">{error.message}</PfNotice> }
  if (loading || !data) return <PfSkeleton height={260} />

  const allKeys = data.keys || []
  const keys = allKeys.filter((k) => serviceOf(k) === tab)
  // [2026-09-25] ADDED — the rows as displayed, in the order the date arrows ask for.
  const shownKeys = sortKeysBy(keys, keySort)
  const activeTab = TABS.find((t) => t.service === tab) || TABS[0]
  const countOf = (service) => allKeys.filter((k) => serviceOf(k) === service).length
  const openCreate = (service) => { setCreatingService(service); setCreating(true) }

  async function create() {
    setBusy(true)
    try {
      const result = await platform.createKey(label, creatingService)
      setSecret(result)
      setCreating(false)
      setLabel('')
      // Show the tab the key was just minted into, so the new row is on screen when the show-once
      // panel closes. A no-op when you created from the tab you were already on.
      setTab(result?.service || creatingService)
      reload()
      toast(`${creatingService === 'proctoring' ? 'API' : 'SDK'} key created`)
    } catch (err) { onError?.(err); toast(err.message, true) }
    finally { setBusy(false) }
  }

  async function revoke(prefix) {
    // [2026-09-05] CHANGED — OLD: if (!window.confirm(`Revoke key ${prefix}? …`)) return
    if (!(await confirm({
      title: 'Revoke this key?',
      subtitle: prefix,
      message: 'Anything using this key stops working within a minute, once the server\u2019s key '
             + 'cache expires. This cannot be undone \u2014 rotate instead if you need a '
             + 'replacement with no downtime.',
      confirmLabel: 'Revoke key',
    }))) return
    try {
      await platform.revokeKey(prefix)
      reload()
      toast('Key revoked')
    } catch (err) { onError?.(err); toast(err.message, true) }
  }

  // [2026-09-03] Delete only ever reaches a REVOKED row — the button is not rendered for an active
  // key, and the server refuses one regardless. The confirmation says what is and is not lost: the
  // key stopped working when it was revoked; this removes the record of it.
  async function remove(row) {
    const live = row.status === 'active'
    // The confirmation states the CONSEQUENCE, and the two consequences are not the same. Deleting a
    // revoked key is bookkeeping; deleting a live one takes a working credential away from whatever
    // is using it right now, and that has to be said before the click, not discovered after.
    // [2026-09-05] CHANGED — same two consequences, same wording, in our own dialog.
    if (!(await confirm({
      title: live ? 'Delete this ACTIVE key?' : 'Remove this key from the list?',
      subtitle: row.key_prefix,
      message: live
        ? 'This key is ACTIVE. It will be revoked and removed \u2014 anything using it stops '
        + 'working, within a minute, once the server\u2019s key cache expires.'
        : 'It was already revoked and stopped working then. This only clears the record.',
      confirmLabel: live ? 'Revoke and delete' : 'Remove from list',
    }))) return
    try {
      await platform.deleteKey(row.key_prefix)
      reload()
      toast(live ? 'Key deleted' : 'Key removed from the list')
    } catch (err) { onError?.(err); toast(err.message, true) }
  }

  // Rotate = mint a new key (carrying the old label), reveal it once, THEN revoke the old one.
  async function rotate(row) {
    // [2026-09-05] CHANGED — OLD: if (!window.confirm(`Rotate key ${row.key_prefix}? …`)) return
    if (!(await confirm({
      title: 'Rotate this key?',
      subtitle: row.key_prefix,
      message: 'A new key is created and shown once, then this one is revoked. Switch your backend '
             + 'to the new key when you have copied it \u2014 there is no window where nothing '
             + 'authenticates, and your credit balance is untouched.',
      confirmLabel: 'Rotate key',
      tone: 'primary',
    }))) return
    try {
      // SERVICE PRESERVED. Rotation replaces a key with the same kind of key; without this the
      // replacement would default to 'sdk' and a rotated API key would come back unable to call
      // the API it was rotating for.
      const result = await platform.createKey(row.label || '', serviceOf(row))
      setSecret(result)
      await platform.revokeKey(row.key_prefix)
      reload()
      toast('Key rotated — old key revoked')
    } catch (err) { onError?.(err); toast(err.message, true) }
  }

  return (
    <>
      <PfCard
        title="API keys"
        subtitle={`${data.active || 0} active of ${allKeys.length}`}
        actions={(
          <div style={{ display: 'inline-flex', gap: 8 }}>
            {TABS.map((t) => (
              <button key={t.service}
                      className={`pf-btn${t.service === tab ? '' : ' ghost'}`}
                      onClick={() => openCreate(t.service)}
                      title={t.blurb}>
                <PlusIcon />{t.create}
              </button>
            ))}
          </div>
        )}
      >
        {/* aria-pressed, NOT role="tab": role="tab" REPLACES a button's implicit role, and the e2e
            suites locate controls with getByRole('button', …). A segmented control written that way
            has already broken every sign-in test once (see CLAUDE.md). */}
        <div className="pf-tabs" role="group" aria-label="Key type">
          {TABS.map((t) => (
            <button key={t.service} type="button"
                    className={`pf-tab${t.service === tab ? ' active' : ''}`}
                    aria-pressed={t.service === tab}
                    onClick={() => setTab(t.service)}>
              {t.label} ({countOf(t.service)})
            </button>
          ))}
        </div>
        <p className="pf-muted" style={{ fontSize: 12.5, margin: '0 0 14px' }}>
          {activeTab.blurb}
        </p>

        {keys.length === 0 ? (
          <PfEmpty title={`No ${activeTab.label.toLowerCase()} yet`}
                   hint={`${activeTab.blurb} Create one with the "${activeTab.create}" button above.`} />
        ) : (
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th>Key</th>
                  {/* The Figma spells this "Lable". Rendered correctly here on purpose — a typo
                      copied out of a mock is still a typo on a customer's screen. */}
                  <th>Label</th>
                  {/* [2026-09-25] CHANGED — both date columns get the up/down sort arrow. Asked for
                      by Rujal: an arrow on every date column, newest / oldest first. "Created by"
                      sorts by the creation date shown under the name; "Last action" by the
                      revocation date.
                      OLD: <th>Created by</th>
                           <th>Last action</th> */}
                  <th>
                    <PfSortHeader label="Created by" sortKey="created_at" sort={keySort}
                                  onSort={(key, dir) => setKeySort({ key, dir })} />
                  </th>
                  <th>
                    <PfSortHeader label="Last action" sortKey="revoked_at" sort={keySort}
                                  onSort={(key, dir) => setKeySort({ key, dir })} />
                  </th>
                  <th>Status</th>
                  <th className="num">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* [2026-09-25] CHANGED — OLD: {keys.map((k) => { — now the sorted copy. */}
                {shownKeys.map((k) => {
                  const active = k.status === 'active'
                  return (
                    <tr key={k.key_prefix}>
                      <td className="pf-mono">{k.key_prefix}</td>
                      <td>{k.label || <span className="pf-muted">—</span>}</td>
                      {/* [2026-09-02] `created_by` and `revoked_at` are shown here; both were
                          already in the API response (app/core/apikeys.py:257-259) and neither was
                          rendered. "Who created this key and when was it revoked" is exactly the
                          question asked during a credential incident. */}
                      <td>
                        {k.created_by || <span className="pf-muted">—</span>}
                        <div className="pf-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                          {fmtDate(k.created_at)}
                        </div>
                      </td>
                      <td>
                        {k.revoked_at ? (
                          <>
                            {k.revoked_reason || 'Revoked'}
                            <div className="pf-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                              {fmtDate(k.revoked_at)}
                            </div>
                          </>
                        ) : <span className="pf-muted">NA</span>}
                      </td>
                      <td>
                        <PfChip tone={STATUS_TONE[k.status] || 'idle'}>
                          {k.status === 'active' ? 'Active' : 'Revoked'}
                        </PfChip>
                      </td>
                      <td className="num">
                        {/* ICON-ONLY BUTTONS, SO EACH ONE CARRIES ITS OWN aria-label AND title.
                            The glyph is aria-hidden (see icons.jsx) — the BUTTON is what speaks.
                            Without these three labels this column is three unnamed buttons to a
                            screen reader, on the screen where one of them destroys a credential. */}
                        <div style={{ display: 'inline-flex', gap: 8 }}>
                          <button className="pf-img-btn" onClick={() => rotate(k)}
                                  disabled={!active}
                                  title={active ? 'Rotate this key' : 'Only an active key can be rotated'}
                                  aria-label={`Rotate key ${k.key_prefix}`}>
                            <img src={rotateKey} alt="" aria-hidden="true" width="34" height="34" />
                          </button>
                          <button className="pf-icon-btn" onClick={() => revoke(k.key_prefix)}
                                  disabled={!active}
                                  title={active ? 'Revoke this key' : 'Already revoked'}
                                  aria-label={`Revoke key ${k.key_prefix}`}>
                            <MinusCircleIcon />
                          </button>
                          {/* [2026-09-03] Delete is offered on ACTIVE keys too. The server revokes
                              before deleting, so the revocation is still logged; requiring
                              revoke-then-delete made clearing a few stale keys twice the clicks and
                              protected only the row, not the audit record. */}
                          <button className="pf-icon-btn danger" onClick={() => remove(k)}
                                  title={active ? 'Revoke and delete this key' : 'Remove this row'}
                                  aria-label={`Delete key ${k.key_prefix}`}>
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </PfCard>

      {creating && (
        <PfModal title={creatingService === 'proctoring' ? 'Create API key' : 'Create SDK key'}
                 subtitle={creatingService === 'proctoring'
                   ? 'For your backend, calling the proctoring API server-to-server.'
                   : 'For the browser SDK on your exam page.'}
                 onClose={() => setCreating(false)}>
          <label className="pf-field">
            <span>Label (optional)</span>
            <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="Enter the key label e.g. prod backend"
                   onKeyDown={(e) => { if (e.key === 'Enter' && !busy) create() }} />
          </label>
          <div className="pf-modal-foot">
            <button className="pf-btn ghost" onClick={() => setCreating(false)}>Cancel</button>
            <button className="pf-btn" disabled={busy} onClick={create}>
              {busy ? 'Creating…' : (creatingService === 'proctoring' ? 'Create API Key' : 'Create SDK Key')}
            </button>
          </div>
        </PfModal>
      )}

      {secret && <SecretModal secret={secret} onClose={() => setSecret(null)} />}
      {confirmDialog}
    </>
  )
}

// [2026-09-04] --- OLD RENDER (pre-revamp), kept per the repo rule. ------------------------------
//
// The screen this file drew before the IncProctor restyle: ui.jsx's <Card>/<Modal>, the shadcn
// <Table> and <Badge>, and text buttons instead of the icon trio. The five mutation functions above
// are unchanged, so restoring this is a straight swap of the two JSX blocks.
//
//   const STATUS_VARIANT = { active: 'healthy', revoked: 'idle' }
//
//   function SecretModal({ secret, onClose }) {
//     return (
//       <Modal title="Your new API key" subtitle="Copy the secret now — it is shown only once." onClose={onClose}>
//         <Notice kind="warn">
//           This secret is not stored and cannot be shown again. If you lose it, create another key.
//         </Notice>
//         <div style={{ marginTop: 12 }}>
//           <label className="muted" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>
//             API key (X-API-Key)
//           </label>
//           <div className="row" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
//             <input readOnly value={secret.api_key} className="mono" style={{ flex: 1 }} />
//             <button className="btn ghost" onClick={() => copy(secret.api_key, 'API key')}>Copy</button>
//           </div>
//         </div>
//         <div style={{ marginTop: 12 }}>
//           <label className="muted" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>
//             Secret (X-API-Secret)
//           </label>
//           <div className="row" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
//             <input readOnly value={secret.secret} className="mono" style={{ flex: 1 }} />
//             <button className="btn ghost" onClick={() => copy(secret.secret, 'Secret')}>Copy</button>
//           </div>
//         </div>
//         <div style={{ marginTop: 16, textAlign: 'right' }}>
//           <button className="btn" onClick={onClose}>Done</button>
//         </div>
//       </Modal>
//     )
//   }
//
//   <Card title="API keys" subtitle={`${data.active || 0} active`}
//         actions={<button className="btn" onClick={() => setCreating(true)}>+ Create key</button>}>
//     {keys.length === 0 ? (
//       <Empty title="No API keys yet"
//              hint="Create one to start calling the proctoring API from your backend." />
//     ) : (
//       <Table>
//         <TableHeader>
//           <TableRow>
//             <TableHead>Key</TableHead><TableHead>Label</TableHead><TableHead>Status</TableHead>
//             <TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead>
//           </TableRow>
//         </TableHeader>
//         <TableBody>
//           {keys.map((k) => {
//             const active = k.status === 'active'
//             return (
//               <TableRow key={k.key_prefix}>
//                 <TableCell className="mono">{k.key_prefix}</TableCell>
//                 <TableCell>{k.label || <span className="muted">—</span>}</TableCell>
//                 <TableCell><Badge variant={STATUS_VARIANT[k.status] || 'idle'}>{k.status}</Badge></TableCell>
//                 <TableCell className="muted">
//                   {fmtDate(k.created_at)}
//                   {k.created_by && <div style={{ fontSize: 11.5, opacity: 0.8 }}>by {k.created_by}</div>}
//                 </TableCell>
//                 <TableCell className="text-right">
//                   {active ? (
//                     <div style={{ display: 'inline-flex', gap: 8 }}>
//                       <button className="btn ghost" onClick={() => rotate(k)}>Rotate</button>
//                       <button className="btn ghost" onClick={() => revoke(k.key_prefix)}>Revoke</button>
//                       <button className="btn ghost" onClick={() => remove(k)}>Delete</button>
//                     </div>
//                   ) : (
//                     <div style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
//                       <span className="muted" style={{ fontSize: 12 }}>
//                         {k.revoked_reason || 'revoked'}
//                         {k.revoked_at && <div style={{ fontSize: 11.5, opacity: 0.8 }}>{fmtDate(k.revoked_at)}</div>}
//                       </span>
//                       <button className="btn ghost" onClick={() => remove(k)}>Delete</button>
//                     </div>
//                   )}
//                 </TableCell>
//               </TableRow>
//             )
//           })}
//         </TableBody>
//       </Table>
//     )}
//   </Card>
//
//   {creating && (
//     <Modal title="Create API key" subtitle="Give it a label so you can tell your keys apart."
//            onClose={() => setCreating(false)}>
//       <label className="muted" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>
//         Label (optional)
//       </label>
//       <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
//              placeholder="e.g. prod backend" style={{ width: '100%', marginTop: 4 }} />
//       <div style={{ marginTop: 16, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
//         <button className="btn ghost" onClick={() => setCreating(false)}>Cancel</button>
//         <button className="btn" disabled={busy} onClick={create}>
//           {busy ? 'Creating…' : 'Create key'}
//         </button>
//       </div>
//     </Modal>
//   )}
//
// --- END OLD RENDER -----------------------------------------------------------------------------
